import utils from '../../utils';

export default {
    namespaced: true,

    state: {

    },

    getters: {
        getAngle360: () => (dx, dy) => {
            let angle = Math.atan(dy / dx) * 180 / Math.PI || 0;

            // horizontal size
            if (dx >= 0) {
                angle += 180;
            }
            // vertical size
            else if (dy >= 0) {
                angle += 360;
            }

            return angle;
        },

        getAngleByPoints: (state, getters) => (p1, p2) => {
            let dx = p1.x - p2.x;
            let dy = p1.y - p2.y;

            return getters.getAngle360(dx, dy);
        },

        getStartAngle: () => (a1, a2) => {
            // const a1 = +this.startAngle;
            // const a2 = +this.endAngle;

            let diff = Math.abs(a1 - a2);

            if (diff <= 180) {
                return Math.min(a1, a2)
            }
            else {
                return Math.max(a1, a2)
            }
        },

        getLineAngle: (state, getters) => (p1, p2) => {
            let dx = p2.x - p1.x;
            let dy = p2.y - p1.y;

            return getters.getAngle360(dx, dy);
        },

        getAngles: (state, getters) => (points) => {
            let angles = [];

            let anglePointsArray = getters.anglePointsArray(points);

            anglePointsArray.forEach(anglePoints => {
                let pointStart  = anglePoints.pointStart;
                let pointCenter = anglePoints.pointCenter;
                let pointEnd    = anglePoints.pointEnd;

                let angleIsHidden = pointCenter.angleIsHidden || false;

                let startAngle = getters.startAngle(pointCenter, pointStart);
                let endAngle = getters.endAngle(pointCenter, pointEnd);

                let angleDirectionStart = getters.angleDirectionStart(anglePoints);
                let angleSizeOrigin = getters.angleSize(anglePoints);
                let angleSize = Math.round(angleSizeOrigin);

                let angle = {
                    id: anglePoints.id,

                    angleIsHidden,

                    pointStart,
                    pointCenter,
                    pointEnd,

                    startAngle,
                    endAngle,

                    angleDirectionStart,
                    angleSize,
                    angleSizeOrigin,
                };

                angles.push(angle);
            });

            return angles;
        },

        anglePointsArray: () => (points) => {

            if (points.length < 3) return [];

            let angles = [];
            let pointStart = points[0];
            let pointCenter = points[1];

            for (let i = 2; i < points.length; i++) {

                let id = pointCenter.id || Date.now() + i;

                let angle = {
                    id,
                    pointStart,
                    pointCenter,
                    pointEnd: points[i],
                };

                angles.push(angle);

                pointStart = points[i - 1];
                pointCenter = points[i];
            }

            return angles;
        },

        startAngle: (state, getters) => (pointCenter, pointStart) => {
            let xSize = pointCenter.x - pointStart.x; // horizontal size
            let ySize = pointCenter.y - pointStart.y; // vertical size

            return getters.getAngle360(xSize, ySize);
        },

        endAngle: (state, getters) => (pointCenter, pointEnd) => {
            let xSize = pointCenter.x - pointEnd.x; // horizontal size
            let ySize = pointCenter.y - pointEnd.y; // vertical size

            return getters.getAngle360(xSize, ySize);
        },

        angleDirectionStart: (state, getters) => ({pointStart, pointCenter, pointEnd}) => {
            const a1 = +getters.startAngle(pointCenter, pointStart);
            const a2 = +getters.endAngle(pointCenter, pointEnd);

            let diff = Math.abs(a1 - a2);

            if (diff <= 180) {
                return Math.min(a1, a2)
            }
            else {
                return Math.max(a1, a2)
            }
        },

        angleSize: (state, getters) => ({pointStart, pointCenter, pointEnd}) => {
            const a1 = +getters.startAngle(pointCenter, pointStart);
            const a2 = +getters.endAngle(pointCenter, pointEnd);

            let angle = Math.abs(a1 - a2);

            if (angle >= 180) {
                angle = 360 - Math.max(a1, a2) + Math.min(a1, a2);
            }

            return angle;
        },

        getEndPointRotation: (state, getters, rootState, rootGetters) => (centerPoint, endPoint, angleDiff) => {
            let line = rootGetters['line/getLineByPoints'](centerPoint, endPoint);

            let length = line.length;
            let angle = line.angle360;

            let newAngle = angle + angleDiff;
            let angleInRad = newAngle / 180 * Math.PI;

            let dx = length * Math.cos(angleInRad);
            let dy = length * Math.sin(angleInRad);

            return {
                ...endPoint,
                x: dx + centerPoint.x,
                y: dy + centerPoint.y,
            };
        },

        getAngleChange: (state, getters, rootState) => (newPoints, sizeDiff) => {
            const pointsLength = newPoints.length;
            let pointStart = newPoints[pointsLength - 2];
            let pointCenter = newPoints[pointsLength - 1];
            let pointEnd = rootState.editor.points[pointsLength];

            let angleStart = getters.getAngleByPoints(pointCenter, pointStart);
            let angleEnd = getters.getAngleByPoints(pointCenter, pointEnd);

            let startAngle = getters.getStartAngle(angleStart, angleEnd);

            let direction = (startAngle === angleStart) ? 1 : -1;

            return sizeDiff * direction;
        },

    },

    mutations: {
        setAngleHidden(state, { anglePoint, hidden }) {
            anglePoint['angleIsHidden'] = hidden;
        },

        setAngleIsHover(state, { point, isHover }) {
            point['isAngleHover'] = isHover;
        },

    },

    actions: {
        rotateTrim({rootState, rootGetters, dispatch}, rotation) {
            const points = rootState['editor'].points;
            const xCenter = rootState['editor'].stageWidth / 2;
            const yCenter = rootState['editor'].stageHeight / 2;

            // do nothing if no lines
            if (!points.length) return;

            // rotate the trim around the center of the canvas
            let centerPoint = {
                x: xCenter,
                y: yCenter
            };

            let newPoints = [];

            // rotate each of the trim points around the center point
            points.forEach(point => {
                let line = rootGetters['line/getLineByPoints'](centerPoint, point);

                let length = line.length;
                let angle = (line.angle360 + rotation);
                let angleInRad = angle / 180 * Math.PI;

                let dx = length * Math.cos(angleInRad);
                let dy = length * Math.sin(angleInRad);

                let newPoint = {
                    ...point,
                    x: dx + xCenter,
                    y: dy + yCenter,
                };

                newPoints.push(newPoint);
            });

            // apply trim points changes
            dispatch('editor/setPoints', newPoints, { root: true })
        },

        setAngleChange({getters, rootState, rootGetters, dispatch}, {pointCenter, angleDiff}) {
            const originalPoints = rootState.editor.points;



            if (!angleDiff) {
                return originalPoints;
            }

            let points = utils.deepCopy(originalPoints);
            let newPoints = [];

            // update all points on new positions

            let centerPointIndex = rootGetters['editor/getPointIndexById'](pointCenter.id);

            for (let i = 0; i <= centerPointIndex; i++) {
                let nextPoint = originalPoints[i];

                newPoints.push(nextPoint);
            }

            // get correct direction of the angle change
            let angleDiffWithDirection = getters.getAngleChange(newPoints, angleDiff)

            for (let i = centerPointIndex + 1; i < points.length; i++) {
                let nextPoint = points[i];

                let newPoint = getters.getEndPointRotation(pointCenter, nextPoint, angleDiffWithDirection);

                newPoints.push(newPoint);
            }

            dispatch('editor/setPoints', newPoints, { root: true });
        },

    }
}